<script setup>
import { ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { mentionChipIcon } from 'shared/helpers/copilotMentionContext';

const props = defineProps({
  showCategories: { type: Boolean, default: false },
  showTypeBadge: { type: Boolean, default: false },
  categories: { type: Array, default: () => [] },
  items: { type: Array, default: () => [] },
  selectedIndex: { type: Number, default: 0 },
  isLoading: { type: Boolean, default: false },
  query: { type: String, default: '' },
});

const emit = defineEmits(['select', 'selectCategory']);

const TYPE_LABEL_KEYS = {
  contact: 'SEARCH.TABS.CONTACTS',
  conversation: 'SEARCH.TABS.CONVERSATIONS',
  article: 'SEARCH.TABS.ARTICLES',
  event: 'SEARCH.TABS.EVENTS',
};

const { t } = useI18n();
const listRef = ref(null);

const adjustScroll = () => {
  nextTick(() => {
    const container = listRef.value;
    if (!container) return;
    const selectedElement = container.querySelector(
      `#copilot-mention-${props.selectedIndex}`
    );
    selectedElement?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
  });
};

function handleCategorySelect(categoryId, event) {
  event.preventDefault();
  event.stopPropagation();
  emit('selectCategory', categoryId);
}

function handleItemSelect(item, event) {
  event.preventDefault();
  event.stopPropagation();
  emit('select', item);
}

function itemSubtitle(item) {
  if (item.subtitle) return item.subtitle;
  if (item.status) return item.status;
  return '';
}

watch(() => props.selectedIndex, adjustScroll);
watch(() => props.items, adjustScroll);
watch(() => props.categories, adjustScroll);
</script>

<template>
  <div
    class="absolute left-0 bottom-full mb-2 w-80 rounded-xl bg-n-solid-2 shadow-lg ring-1 ring-n-weak z-[100] overflow-hidden"
    data-mention-menu
    @mousedown.stop
  >
    <div ref="listRef" class="max-h-56 overflow-y-auto p-1.5">
      <template v-if="showCategories">
        <button
          v-for="(category, index) in categories"
          :id="`copilot-mention-${index}`"
          :key="category.id"
          type="button"
          class="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg text-left text-sm transition-colors"
          :class="
            selectedIndex === index
              ? 'bg-n-brand/10 text-n-brand'
              : 'text-n-slate-11 hover:bg-n-solid-3 hover:text-n-slate-12'
          "
          @mousedown="handleCategorySelect(category.id, $event)"
        >
          <span class="size-4 shrink-0" :class="category.icon" />
          <span class="flex-1 min-w-0 font-medium">
            {{ t(category.labelKey) }}
          </span>
          <span class="i-lucide-chevron-right size-4 shrink-0 text-n-slate-9" />
        </button>
      </template>

      <template v-else>
        <div
          v-if="isLoading"
          class="flex items-center justify-center gap-2 px-3 py-4 text-sm text-n-slate-9"
        >
          <span class="i-lucide-loader-2 size-4 animate-spin" />
          {{ t('COPILOT.MENTION_LOADING') }}
        </div>
        <template v-else>
          <button
            v-for="(item, index) in items"
            :id="`copilot-mention-${index}`"
            :key="`${item.type}:${item.id}`"
            type="button"
            class="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg text-left text-sm transition-colors"
            :class="
              selectedIndex === index
                ? 'bg-n-brand/10 text-n-brand'
                : 'text-n-slate-11 hover:bg-n-solid-3 hover:text-n-slate-12'
            "
            @mousedown="handleItemSelect(item, $event)"
          >
            <span
              class="size-4 shrink-0 text-n-slate-10"
              :class="mentionChipIcon(item.type)"
            />
            <span class="flex-1 min-w-0">
              <span class="flex items-center gap-2">
                <span class="block font-medium truncate">{{ item.name }}</span>
                <span
                  v-if="showTypeBadge && TYPE_LABEL_KEYS[item.type]"
                  class="shrink-0 text-[10px] uppercase tracking-wide text-n-slate-9"
                >
                  {{ t(TYPE_LABEL_KEYS[item.type]) }}
                </span>
              </span>
              <span
                v-if="itemSubtitle(item) || item.date"
                class="block text-xs text-n-slate-9 truncate"
              >
                <template v-if="itemSubtitle(item)">
                  {{ itemSubtitle(item) }}
                </template>
                <template v-if="itemSubtitle(item) && item.date"> · </template>
                <template v-if="item.date">{{ item.date }}</template>
              </span>
            </span>
          </button>
          <div
            v-if="items.length === 0"
            class="px-3 py-4 text-center text-sm text-n-slate-9"
          >
            {{
              query
                ? t('COPILOT.MENTION_NO_RESULTS')
                : t('COPILOT.MENTION_EMPTY')
            }}
          </div>
        </template>
      </template>
    </div>
  </div>
</template>
